import React, { useEffect, useRef, useState } from 'react';
import SignaturePad from './SignaturePad';
import {
  TEMPLATE_URL, MAX_TRACKS, buildSignedForm, bytesToBase64, downloadPdf,
  formatDateIL, isValidIsraeliId,
} from '../utils/distributionFormPdf';

const F = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const C = {
  bg: '#000', surface: '#0b0b0b', border: 'rgba(240,237,232,0.12)', borderHi: 'rgba(240,237,232,0.4)',
  text: '#f0ede8', muted: 'rgba(240,237,232,0.45)', red: 'rgba(255,120,120,0.95)', green: 'rgba(100,255,180,0.9)',
};

const inputBase = {
  width: '100%', boxSizing: 'border-box', background: '#0d0d0d', border: `1px solid ${C.border}`,
  color: C.text, fontFamily: F, fontSize: '15px', padding: '11px 12px', borderRadius: '6px',
  outline: 'none', direction: 'rtl', textAlign: 'right',
};

function Label({ children, htmlFor }) {
  return <label htmlFor={htmlFor} style={{ display: 'block', fontFamily: F, fontSize: '12px', color: C.muted, marginBottom: '6px' }}>{children}</label>;
}

function SectionTitle({ children, hint }) {
  return (
    <div style={{ margin: '28px 0 12px' }}>
      <p style={{ fontFamily: F, fontSize: '13px', fontWeight: 700, color: C.text }}>{children}</p>
      {hint && <p style={{ fontFamily: F, fontSize: '12px', color: C.muted, marginTop: '4px', lineHeight: 1.6 }}>{hint}</p>}
    </div>
  );
}

// storePerformer = name as it appears in stores (sent to PIL); the deed itself uses the legal name.
const emptyRow = stage => ({ track: '', release: '', storePerformer: stage || '' });

// Full legal name in Hebrew: Hebrew letters only (plus spaces, hyphen, geresh), first + last name.
const HEB_NAME_RE = /^[\u05D0-\u05EA][\u05D0-\u05EA'"׳״\- ]*$/;
function isHebrewFullName(v) {
  const t = v.trim().replace(/\s+/g, ' ');
  return HEB_NAME_RE.test(t) && t.split(' ').length >= 2;
}

export default function DistributionFormSigner({ artistId, artistName, releaseTitles = [], onClose, onSubmitted }) {
  const [fullName, setFullName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [idTouched, setIdTouched] = useState(false);
  const [rows, setRows] = useState([emptyRow(artistName)]);
  const [notes, setNotes] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [busy, setBusy] = useState(null); // null | 'submit' | 'download'
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // null | 'submitted' | 'downloaded'
  const sigRef = useRef(null);

  // Lock background scroll while the overlay is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const idValid = isValidIsraeliId(idNumber);
  const filledRows = rows.filter(r => r.track.trim());

  function updateRow(i, key, val) {
    setRows(prev => prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  }
  function addRow() {
    setRows(prev => (prev.length >= MAX_TRACKS ? prev : [...prev, emptyRow(artistName)]));
  }
  function removeRow(i) {
    setRows(prev => (prev.length === 1 ? [emptyRow(artistName)] : prev.filter((_, idx) => idx !== i)));
  }

  function validate() {
    if (!fullName.trim()) return 'יש למלא שם מלא';
    if (!isHebrewFullName(fullName)) return 'יש למלא שם פרטי ושם משפחה בעברית, כפי שמופיע בתעודת הזהות';
    if (!idValid) return 'מספר תעודת הזהות אינו תקין';
    if (!filledRows.length) return 'יש להוסיף לפחות הקלטה אחת';
    if (sigEmpty) return 'יש לחתום בתיבת החתימה';
    if (!agreed) return 'יש לאשר שקראת את הטופס';
    return '';
  }

  async function finish(mode) {
    const msg = validate();
    if (msg) { setError(msg); return; }
    setError('');
    setBusy(mode);
    try {
      const signedAt = new Date();
      const legalName = fullName.trim().replace(/\s+/g, ' ');
      const tracks = filledRows.map(r => ({ performer: legalName, storePerformer: (r.storePerformer || artistName || '').trim(), track: r.track.trim(), release: r.release.trim() }));
      const pdfBytes = await buildSignedForm({
        fullName: fullName.trim(),
        idNumber: idNumber.trim(),
        tracks,
        notes: notes.trim(),
        signaturePng: sigRef.current.toPngBytes(),
        signedAt,
      });
      const filename = `YEN_טופס_הפצה_${fullName.trim().replace(/[\\/:*?"<>|]/g, '')}_${formatDateIL(signedAt).replace(/\//g, '-')}.pdf`;

      if (mode === 'submit') {
        const res = await fetch('/api/signed-forms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'submit',
            artistId,
            artistName,
            signerName: fullName.trim(),
            idLast4: idNumber.trim().slice(-4),
            tracks,
            notes: notes.trim(),
            pdfBase64: bytesToBase64(pdfBytes),
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || 'השליחה נכשלה');
        downloadPdf(pdfBytes, filename); // the artist keeps a copy too
        onSubmitted && onSubmitted(tracks.length);
        setDone('submitted');
      } else {
        downloadPdf(pdfBytes, filename);
        setDone('downloaded');
      }
    } catch (e) {
      setError(e.message || 'משהו השתבש, נסו שוב');
    }
    setBusy(null);
  }

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 1100, background: C.bg, overflowY: 'auto',
    WebkitOverflowScrolling: 'touch', direction: 'rtl', color: C.text,
  };

  if (done) {
    return (
      <div style={overlay}>
        <div style={{ maxWidth: '480px', margin: '0 auto', padding: 'calc(90px + env(safe-area-inset-top)) 24px 40px', textAlign: 'center' }}>
          <p style={{ fontSize: '34px', marginBottom: '16px', color: C.green }}>✓</p>
          <h2 style={{ fontFamily: F, fontSize: '19px', fontWeight: 700, marginBottom: '12px' }}>
            {done === 'submitted' ? 'הטופס נשלח לאישור' : 'הטופס החתום ירד למכשיר'}
          </h2>
          <p style={{ fontFamily: F, fontSize: '14px', color: C.muted, lineHeight: 1.8, marginBottom: '32px' }}>
            {done === 'submitted'
              ? 'עותק חתום נשמר גם אצלך. לאחר בדיקה, YEN SOUND תחתום מצידה על נספח ב׳.'
              : 'הטופס לא נשלח. ניתן לשלוח אותו אלינו בכל דרך שנוחה לך.'}
          </p>
          <button onClick={onClose} style={{ fontFamily: F, fontSize: '14px', fontWeight: 600, padding: '13px 32px', background: C.text, color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            סגירה
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay}>
      {/* header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: C.bg, borderBottom: `1px solid ${C.border}`, padding: 'calc(14px + env(safe-area-inset-top)) 20px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontFamily: F, fontSize: '15px', fontWeight: 700 }}>טופס הפצה</p>
          <p style={{ fontFamily: F, fontSize: '11px', color: C.muted, marginTop: '2px' }}>הצהרה בדבר העברת זכויות יוצרים בתקליט</p>
        </div>
        <button onClick={onClose} aria-label="סגירה" style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, width: '36px', height: '36px', borderRadius: '6px', fontSize: '16px', cursor: 'pointer' }}>✕</button>
      </div>

      <div style={{ maxWidth: '480px', margin: '0 auto', padding: '8px 20px 60px' }}>
        <p style={{ fontFamily: F, fontSize: '13px', color: C.muted, lineHeight: 1.8, marginTop: '18px' }}>
          ממלאים כמה פרטים, חותמים פעם אחת, והחתימה והתאריך ימולאו אוטומטית בכל המקומות בטופס.
          {' '}<a href={TEMPLATE_URL} target="_blank" rel="noreferrer" style={{ color: C.text }}>לקריאת הטופס המלא ↗</a>
        </p>

        {/* signer */}
        <SectionTitle>פרטי החותם/ת</SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <Label htmlFor="dfs-name">שם מלא בעברית (כפי שמופיע בתעודת הזהות)</Label>
            <input id="dfs-name" value={fullName} onChange={e => setFullName(e.target.value)} autoComplete="name" placeholder="שם פרטי ושם משפחה" style={inputBase} />
            <p style={{ fontFamily: F, fontSize: '11px', color: C.muted, marginTop: '6px' }}>השם ימולא גם כשם המבצע בכל ההקלטות בנספח א׳.</p>
          </div>
          <div>
            <Label htmlFor="dfs-id">מספר תעודת זהות</Label>
            <input id="dfs-id" value={idNumber} inputMode="numeric" maxLength={9} autoComplete="off"
              onChange={e => setIdNumber(e.target.value.replace(/\D/g, ''))}
              onBlur={() => setIdTouched(true)}
              style={{ ...inputBase, direction: 'ltr', textAlign: 'right', borderColor: idTouched && idNumber && !idValid ? 'rgba(255,120,120,0.6)' : C.border }} />
            {idTouched && idNumber && !idValid && <p style={{ fontFamily: F, fontSize: '12px', color: C.red, marginTop: '6px' }}>מספר תעודת זהות לא תקין</p>}
          </div>
        </div>

        {/* tracks */}
        <SectionTitle hint={`עד ${MAX_TRACKS} הקלטות. כל שורה תופיע בטבלה בנספח א׳.`}>פירוט ההקלטות</SectionTitle>
        <datalist id="dfs-releases">
          {releaseTitles.map(t => <option key={t} value={t} />)}
        </datalist>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {rows.map((r, i) => (
            <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: '8px', padding: '12px', background: C.surface }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontFamily: F, fontSize: '12px', fontWeight: 700, color: C.muted }}>הקלטה {i + 1}</span>
                {(rows.length > 1 || r.track) && (
                  <button onClick={() => removeRow(i)} aria-label={`הסרת הקלטה ${i + 1}`} style={{ background: 'none', border: 'none', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: F }}>הסרה</button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <input aria-label="שם ההקלטה" placeholder="שם ההקלטה" value={r.track} onChange={e => updateRow(i, 'track', e.target.value)} style={inputBase} />
                <input aria-label="שם התקליט" placeholder="שם התקליט (אופציונלי)" list="dfs-releases" value={r.release} onChange={e => updateRow(i, 'release', e.target.value)} style={inputBase} />
                <div>
                  <Label>שם המבצע כפי שיופיע בחנויות</Label>
                  <input aria-label="שם המבצע כפי שיופיע בחנויות" placeholder="למשל: Sighdafekt feat. Shower" value={r.storePerformer} onChange={e => updateRow(i, 'storePerformer', e.target.value)} style={inputBase} />
                </div>
              </div>
            </div>
          ))}
        </div>
        {rows.length < MAX_TRACKS && (
          <button onClick={addRow} style={{ marginTop: '10px', width: '100%', padding: '12px', background: 'transparent', border: `1px dashed ${C.border}`, color: C.text, fontFamily: F, fontSize: '13px', borderRadius: '8px', cursor: 'pointer' }}>
            + הוספת הקלטה
          </button>
        )}

        <div style={{ marginTop: '14px' }}>
          <Label htmlFor="dfs-notes">הערות (אופציונלי)</Label>
          <input id="dfs-notes" value={notes} maxLength={110} onChange={e => setNotes(e.target.value)} style={inputBase} />
        </div>

        {/* signature */}
        <SectionTitle hint="החתימה תופיע בעמוד ההצהרה, בנספח א׳ ובנספח ב׳.">חתימה</SectionTitle>
        <SignaturePad ref={sigRef} onChange={setSigEmpty} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
          <span style={{ fontFamily: F, fontSize: '12px', color: C.muted }}>תאריך: {formatDateIL()}</span>
          <button onClick={() => sigRef.current?.clear()} style={{ background: 'none', border: 'none', color: C.muted, fontFamily: F, fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>ניקוי חתימה</button>
        </div>

        <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '22px', cursor: 'pointer' }}>
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: '3px', width: '18px', height: '18px', flexShrink: 0, accentColor: '#f0ede8' }} />
          <span style={{ fontFamily: F, fontSize: '13px', lineHeight: 1.7 }}>קראתי את הטופס על נספחיו, הפרטים שמילאתי נכונים, ואני מאשר/ת את החתימה הדיגיטלית.</span>
        </label>

        {error && (
          <div role="alert" style={{ marginTop: '18px', padding: '11px 14px', border: '1px solid rgba(255,120,120,0.35)', background: 'rgba(255,120,120,0.06)', borderRadius: '6px' }}>
            <p style={{ fontFamily: F, fontSize: '13px', color: C.red }}>{error}</p>
          </div>
        )}

        {/* FINISH */}
        <button onClick={() => finish('submit')} disabled={!!busy}
          style={{ marginTop: '22px', width: '100%', padding: '15px', background: C.text, color: '#000', border: 'none', borderRadius: '6px', fontFamily: F, fontSize: '15px', fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}>
          {busy === 'submit' ? 'שולח…' : 'סיום ושליחה לאישור'}
        </button>
        <p style={{ fontFamily: F, fontSize: '11px', color: C.muted, textAlign: 'center', marginTop: '8px' }}>עותק חתום יירד גם למכשיר שלך</p>
        <button onClick={() => finish('download')} disabled={!!busy}
          style={{ marginTop: '14px', width: '100%', padding: '13px', background: 'transparent', color: C.text, border: `1px solid ${C.border}`, borderRadius: '6px', fontFamily: F, fontSize: '14px', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}>
          {busy === 'download' ? 'מכין קובץ…' : 'סיום והורדה בלבד'}
        </button>
      </div>
    </div>
  );
}
